import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { summaryCardTheme as t } from "./styles";

const formatCurrency = (value) =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : "$0.00";

const kpiConfig = (overall) => [
  {
    label: "Total ARNs",
    value: overall?.arns_total ?? 0,
    tooltip: "Total number of ARNs scanned across all services",
  },
  {
    label: "Security check Findings",
    value: overall?.arns_security_failed ?? 0,
    tooltip: "ARNs with at least one security (CSPM) finding",
    accent: "issue",
  },
  {
    label: "Control ID Findings",
    value: overall?.arns_compliance_failed ?? 0,
    tooltip: "ARNs with at least one compliance control ID finding",
    accent: "warning",
  },
  {
    label: "Compliance Standards",
    value: overall?.compliance_standards_count ?? overall?.compliance_standards ?? null,
    tooltip: "Compliance standards applied (when available from API)",
    emptyLabel: "—",
  },
  {
    label: "Cloud Waste",
    value: overall?.arns_cost ?? 0,
    tooltip: "Total count of ARNs from Cost Optimization data",
    accent: "success",
  },
  {
    label: "Cost (MTD)",
    value: overall?.cost_unblended_mtd_total ?? 0,
    formatter: formatCurrency,
    tooltip: "Unblended cost month-to-date across scanned ARNs",
    accent: "success",
  },
  {
    label: "Security+Compliance+Cost Findings",
    value: overall?.arns_all_three ?? 0,
    tooltip: "ARNs common to CSPM, Compliance and Cost Optimization findings",
    accent: "issue",
  },
];

function SummaryKpiCards({ overall, compact }) {
  const items = kpiConfig(overall);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: compact ? "repeat(4, 1fr)" : "repeat(4, 1fr)",
          lg: compact ? "repeat(5, 1fr)" : "repeat(7, 1fr)",
        },
        gap: compact ? 1 : 2,
        fontFamily: t.fontFamily,
      }}
    >
      {items.map((item) => {
        const hasValue = item.value != null && item.value !== "" && (typeof item.value !== "number" || !Number.isNaN(item.value));
        const displayValue =
          item.emptyLabel != null && !hasValue
            ? item.emptyLabel
            : typeof item.formatter === "function"
            ? item.formatter(item.value)
            : item.value;
        const color =
          item.accent === "issue"
            ? t.colors.issue
            : item.accent === "warning"
            ? t.colors.warning
            : item.accent === "success"
            ? t.colors.success
            : t.colors.text;

        return (
          <Tooltip key={item.label} title={item.tooltip} arrow placement="top">
            <Box
              sx={{
                backgroundColor: compact ? "transparent" : t.colors.card,
                border: compact ? "none" : `1px solid ${t.colors.border}`,
                borderRadius: t.radius.lg,
                p: compact ? 0.5 : 2,
                boxShadow: compact ? "none" : t.shadow,
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                "&:hover": compact ? {} : {
                  borderColor: t.colors.borderLight,
                  boxShadow:
                    "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: t.colors.textMuted,
                  fontSize: compact ? "0.65rem" : "0.75rem",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: compact ? 0 : 0.5,
                  fontFamily: t.fontFamily,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color,
                  fontWeight: 700,
                  fontSize: compact ? "0.9375rem" : "1.25rem",
                  fontFamily: t.fontFamily,
                }}
              >
                {displayValue}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export default SummaryKpiCards;
